import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../../Contexts/AuthContext";
import { addUser, getUserById, updateUserAdmin } from "../../../api/usersApi";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";
import "./styles/addEdit.css";
import { useTranslation } from "react-i18next";
import { FiUser, FiMail, FiLock, FiShield, FiChevronLeft, FiCheckCircle, FiLoader, FiSave, FiUserPlus } from "react-icons/fi";

export function AddEditUser() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    const isEdit = !!id;
    const isOwnAccount = isEdit && user && user.id && String(user.id) === String(id);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(isEdit);

    const [form, setForm] = useState({
        nom: "",
        email: "",
        password: "",
    });

    useEffect(() => {
        if (isEdit) {
            setLoading(true);
            getUserById(id).then(data => {
                setForm({
                    nom: data.nom || "",
                    email: data.email || "",
                    password: ""
                });
            }).finally(() => setLoading(false));
        }
    }, [id, isEdit]);

    function handleChange(e) {
        setForm({ ...form, [e.target.name]: e.target.value });
    }

    function handleSubmit(e) {
        e.preventDefault();
        setSubmitting(true);
        const action = isEdit
            ? updateUserAdmin(id, form)
            : addUser(form);

        action
            .then(() => navigate("/admin/manage-users"))
            .catch(err => {
                console.error(err);
                swal(t("addEditUser.errors.title"), t("addEditUser.errors.invalidInput"), "error");
            })
            .finally(() => setSubmitting(false));
    }

    if (loading) {
        return (
            <div className="uf-page">
                <div className="uf-loading">
                    <FiLoader className="uf-loading-icon" />
                    <span>{t("addEditUser.loading") || "Loading…"}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="uf-page">
            {/* Header */}
            <div className="uf-header">
                <button className="uf-back-btn" onClick={() => navigate("/admin/manage-users")}>
                    <FiChevronLeft /> {t("addEditUser.back") || "Back"}
                </button>
                <div className="uf-header-content">
                    <div className="uf-header-icon">
                        {isEdit ? <FiSave /> : <FiUserPlus />}
                    </div>
                    <div>
                        <h1 className="uf-title">
                            {isEdit ? t("addEditUser.titleEdit") : t("addEditUser.titleAdd")}
                        </h1>
                        <p className="uf-subtitle">
                            {isEdit
                                ? (t("addEditUser.subtitleEdit") || "Update user information")
                                : (t("addEditUser.subtitleAdd") || "Create a new team member")}
                        </p>
                    </div>
                </div>
            </div>

            {/* Form Card */}
            <form onSubmit={handleSubmit} className="uf-form">
                <div className="uf-section">
                    <div className="uf-section-header">
                        <div className="uf-section-icon"><FiUser /></div>
                        <h2 className="uf-section-title">{t("addEditUser.sectionInfo") || "User Information"}</h2>
                    </div>

                    <div className="uf-fields">
                        {/* Name */}
                        <div className="uf-field">
                            <label className="uf-label uf-required">
                                <FiUser className="uf-label-icon" />
                                {t("addEditUser.fields.name")}
                            </label>
                            <input
                                type="text"
                                name="nom"
                                className="uf-input"
                                placeholder={t("addEditUser.placeholders.name") || "Full name"}
                                value={form.nom}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        {/* Email */}
                        <div className="uf-field">
                            <label className="uf-label uf-required">
                                <FiMail className="uf-label-icon" />
                                {t("addEditUser.fields.email")}
                            </label>
                            <input
                                type="email"
                                name="email"
                                className="uf-input"
                                placeholder={t("addEditUser.placeholders.email") || "email@example.com"}
                                value={form.email}
                                onChange={handleChange}
                                required
                                disabled={isOwnAccount}
                            />
                        </div>

                        {/* Role */}
                        <div className="uf-field">
                            <label className="uf-label">
                                <FiShield className="uf-label-icon" />
                                {t("addEditUser.fields.role")}
                            </label>
                            <div className="uf-role-badge">
                                <FiShield />
                                {form.role || t("addEditUser.fields.defaultRole")}
                            </div>
                        </div>

                        {/* Password */}
                        <div className="uf-field">
                            <label className="uf-label" htmlFor="password">
                                <FiLock className="uf-label-icon" />
                                {t("addEditUser.fields.password")}
                                {isEdit && <span className="uf-hint">{t("addEditUser.fields.passwordHint")}</span>}
                            </label>
                            <input
                                type="password"
                                name="password"
                                id="password"
                                className="uf-input"
                                placeholder={isEdit ? "••••••••" : t("addEditUser.placeholders.password") || "Password"}
                                value={form.password}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="uf-actions">
                    <button
                        type="button"
                        className="uf-btn-secondary"
                        onClick={() => navigate("/admin/manage-users")}
                    >
                        <FiChevronLeft /> {t("addEditUser.buttons.cancel") || "Cancel"}
                    </button>
                    <button type="submit" className="uf-btn-primary" disabled={submitting}>
                        {submitting
                            ? <><FiLoader className="uf-btn-spin" /> {t("addEditUser.buttons.saving") || "Saving…"}</>
                            : <><FiCheckCircle /> {isEdit ? t("addEditUser.buttons.save") : t("addEditUser.buttons.create")}</>
                        }
                    </button>
                </div>
            </form>
        </div>
    );
}
